import React from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Avatar,
  Badge,
  LinearProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import TimelineIcon from "@mui/icons-material/Timeline";
import WarningIcon from "@mui/icons-material/Warning";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ApartmentIcon from "@mui/icons-material/Apartment";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import RuleRoundedIcon from "@mui/icons-material/RuleRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import StairsRoundedIcon from "@mui/icons-material/StairsRounded";

export default function HeaderHero({
  appraisal,
  workflow,
  employee,
  statusChipProps,
  latestShownText,
  latestSubmission,
  stats,
  isEditable,
  isOnline,
  requirements,
  onBack,
  onRefresh,
  projectName,
}) {
  const theme = useTheme();

  const getStatusColor = (status) => {
    const colors = {
      draft: theme.palette.grey[500],
      pending: theme.palette.warning.main,
      in_progress: theme.palette.info.main,
      completed: theme.palette.success.main,
      cancelled: theme.palette.error.main,
    };
    return colors[status] || theme.palette.grey[500];
  };

  const resolvedProjectName =
    projectName ||
    appraisal?.project_name ||
    appraisal?.project?.name ||
    employee?.project_name ||
    employee?.project?.name ||
    appraisal?.project ||
    "-";

  const statusColor = getStatusColor(appraisal?.status);
  const progress = Math.max(0, Math.min(100, Number(stats?.completionProgress) || 0));

  /* ---- shared visual atoms -------------------------------------------- */

  const metaPillSx = {
    minWidth: 0,
    px: 1.1,
    py: 0.65,
    borderRadius: "8px",
    border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
    backgroundColor: alpha(theme.palette.background.paper, 0.66),
    backdropFilter: "blur(6px)",
    transition: "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
    "&:hover": {
      borderColor: alpha(theme.palette.primary.main, 0.28),
      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
    },
  };

  const statChip = (label, color, Icon) => (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 16, color: `${color} !important` }} />}
      label={label}
      sx={{
        height: 28,
        borderRadius: "8px",
        fontWeight: 800,
        color,
        backgroundColor: alpha(color, 0.1),
        border: `1px solid ${alpha(color, 0.2)}`,
        "& .MuiChip-label": { px: 0.9 },
        transition: "transform 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: `0 6px 14px ${alpha(color, 0.16)}`,
        },
      }}
    />
  );

  /* small labelled info tile used in the meta strip */
  const InfoTile = ({ icon: Icon, tone, label, value, title }) => (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        flex: { xs: "1 1 45%", sm: 1 },
        minWidth: 0,
        p: 1,
        borderRadius: "8px",
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        background: alpha(theme.palette.background.paper, 0.45),
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 28,
          height: 28,
          borderRadius: "8px",
          bgcolor: alpha(tone, 0.12),
          color: tone,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.1} noWrap>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={800} noWrap title={title}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ mb: { xs: 1.5, md: 1.75 } }}>
      {!isOnline && (
        <Alert
          severity="warning"
          icon={<CloudOffIcon />}
          sx={{
            mb: 1.5,
            borderRadius: "8px",
            alignItems: "center",
            borderLeft: `4px solid ${theme.palette.warning.main}`,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
            background: `linear-gradient(120deg,
              ${alpha(theme.palette.warning.light, 0.24)} 0%,
              ${alpha(theme.palette.warning.main, 0.08)} 100%)`,
          }}
        >
          <Typography variant="body2" fontWeight={700}>
            You are offline. Changes will be saved locally but cannot be submitted until you reconnect.
          </Typography>
        </Alert>
      )}

      <Box
        sx={{
          position: "relative",
          borderRadius: "8px",
          overflow: "hidden",
          background: `linear-gradient(150deg,
            ${alpha(statusColor, 0.12)} 0%,
            ${alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.97 : 0.86)} 46%,
            ${alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.97 : 0.86)} 100%)`,
          border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.18)}`,
          boxShadow: theme.hr?.shadow?.soft || theme.shadows[1],
          /* diagonal animated accent bar on top, status colored */
          "&:before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg,
              ${alpha(statusColor, 0.25)} 0%,
              ${statusColor} 32%,
              ${theme.palette.primary.main} 66%,
              ${alpha(theme.palette.secondary.main, 0.7)} 100%)`,
            backgroundSize: "220% 100%",
            animation: "heroAccentSlide 6s ease-in-out infinite",
          },
          /* soft status glow in the corner */
          "&:after": {
            content: '""',
            position: "absolute",
            width: 240,
            height: 240,
            right: -110,
            top: -130,
            borderRadius: "50%",
            background: `radial-gradient(circle,
              ${alpha(statusColor, 0.22)} 0%,
              transparent 70%)`,
            pointerEvents: "none",
          },
          "@keyframes heroAccentSlide": {
            "0%, 100%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
          },
        }}
      >
        {/* floating version chip (desktop only) */}
        <Box
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            zIndex: 2,
          }}
        >
          {latestSubmission?.version && (
            <Chip
              size="small"
              label={`v${latestSubmission.version}`}
              sx={{
                fontSize: "0.66rem",
                height: 22,
                borderRadius: "8px",
                backgroundColor: alpha(theme.palette.info.main, 0.14),
                color: theme.palette.info.main,
                border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                fontWeight: 800,
              }}
            />
          )}
        </Box>

        <Stack spacing={1.5} sx={{ position: "relative", zIndex: 1, p: { xs: 1.6, md: 2 } }}>
          {/* ===== TOP ROW: identity + actions ===== */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "flex-start" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
              <IconButton
                onClick={onBack}
                size="small"
                aria-label="Back"
                sx={{
                  flexShrink: 0,
                  borderRadius: "8px",
                  border: `1px solid ${alpha(theme.palette.divider, 0.24)}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.9),
                  mt: 0.25,
                  transition: "all 180ms ease",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.background.paper, 1),
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                    transform: "translateX(-2px)",
                  },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* title row */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.1 }}>
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.18)}`,
                    }}
                  >
                    <AssessmentIcon fontSize="small" />
                  </Avatar>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 850,
                      letterSpacing: -0.4,
                      color: theme.palette.primary.main,
                      fontSize: { xs: "1.15rem", md: "1.4rem" },
                    }}
                  >
                    Evaluate Appraisal
                  </Typography>
                  <Badge
                    badgeContent={`#${appraisal?.id ?? "-"}`}
                    color="primary"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        height: 20,
                        minWidth: 44,
                        borderRadius: "8px",
                        position: "static",
                        transform: "none",
                        ml: 0.5,
                      },
                    }}
                  />
                </Stack>

                {/* meta pills: employee, workflow, project, status */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  useFlexGap
                  sx={{ flexWrap: "wrap", rowGap: 1 }}
                >
                  {/* employee — prominent, larger avatar */}
                  <Stack direction="row" spacing={1.1} alignItems="center" sx={metaPillSx}>
                    <Avatar
                      sx={{
                        width: 38,
                        height: 38,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                        fontSize: 16,
                        fontWeight: 800,
                        boxShadow: `0 3px 10px ${alpha(theme.palette.common.black, 0.12)}`,
                      }}
                    >
                      {employee?.name?.[0]?.toUpperCase() || "E"}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={800} noWrap>
                        {employee?.name || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {employee?.employee_code || "-"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={0.9} alignItems="center" sx={metaPillSx}>
                    <TimelineIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {workflow?.name || "Appraisal"}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={0.9} alignItems="center" sx={metaPillSx}>
                    <ApartmentIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, maxWidth: { xs: 180, sm: 260, md: 420 } }}
                      noWrap
                      title={String(resolvedProjectName || "")}
                    >
                      {resolvedProjectName}
                    </Typography>
                  </Stack>

                  <Chip
                    size="small"
                    label={statusChipProps?.label || "-"}
                    sx={{
                      height: 30,
                      backgroundColor: alpha(statusColor, 0.12),
                      color: statusColor,
                      fontWeight: 900,
                      borderRadius: "8px",
                      border: `1px solid ${alpha(statusColor, 0.32)}`,
                      boxShadow: `0 0 0 4px ${alpha(statusColor, 0.06)}`,
                    }}
                  />

                  {isEditable && requirements?.totalMissing > 0 && (
                    <Chip
                      size="small"
                      label={`${requirements.totalMissing} required`}
                      color="warning"
                      icon={<WarningIcon fontSize="small" />}
                      sx={{ height: 30, fontWeight: 800, borderRadius: "8px" }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* actions: last-updated chip + refresh glow button */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                justifyContent: { xs: "space-between", md: "flex-end" },
                flexShrink: 0,
                pt: { xs: 0.5, md: 0 },
              }}
            >
              {latestSubmission && (
                <Tooltip title={`Last updated: ${new Date(latestSubmission.submitted_at).toLocaleString()}`}>
                  <Chip
                    size="small"
                    variant="filled"
                    label={latestShownText}
                    icon={<CalendarTodayIcon fontSize="small" />}
                    sx={{
                      borderRadius: "8px",
                      border: `1px solid ${alpha(theme.palette.divider, 0.24)}`,
                      bgcolor: alpha(theme.palette.background.paper, 0.74),
                      fontWeight: 700,
                    }}
                  />
                </Tooltip>
              )}

              <Tooltip title="Refresh">
                <IconButton
                  onClick={onRefresh}
                  size="small"
                  aria-label="Refresh"
                  sx={{
                    borderRadius: "8px",
                    border: `1px solid ${alpha(theme.palette.divider, 0.24)}`,
                    backgroundColor: alpha(theme.palette.background.paper, 0.84),
                    transition: "all 200ms ease",
                    "&:hover": {
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                      backgroundColor: alpha(theme.palette.background.paper, 1),
                      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.14)}`,
                      "& svg": { transform: "rotate(90deg)" },
                    },
                    "& svg": { transition: "transform 280ms ease" },
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* ===== PROGRESS + STATS PANEL ===== */}
          <Box
            sx={{
              mt: 0.25,
              p: { xs: 1.2, sm: 1.4 },
              borderRadius: "8px",
              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
              background: `linear-gradient(145deg,
                ${alpha(theme.palette.background.paper, 0.72)} 0%,
                ${alpha(theme.palette.background.default, 0.64)} 100%)`,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="baseline">
                <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                  Evaluation Progress
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={900}
                  sx={{ color: theme.palette.primary.main, lineHeight: 1 }}
                >
                  {progress}%
                </Typography>
              </Stack>
              <Chip
                size="small"
                label={`${stats?.answered ?? 0}/${stats?.total ?? 0} questions`}
                sx={{
                  height: 24,
                  fontWeight: 700,
                  borderRadius: "8px",
                  backgroundColor: alpha(theme.palette.text.primary, 0.05),
                  color: "text.secondary",
                }}
              />
            </Stack>

            {/* prominent 8px gradient progress bar with rounded caps */}
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 999,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${
                    theme.palette.secondary?.main || theme.palette.primary.dark
                  })`,
                  transition: "transform 420ms ease",
                },
              }}
            />

            {/* stat pills with icon accents */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1.4, flexWrap: "wrap", gap: 0.75 }}
              alignItems="center"
            >
              {statChip(`${stats?.answered ?? 0} answered`, theme.palette.success.main, CheckCircleRoundedIcon)}
              {statChip(`${stats?.modified ?? 0} modified`, theme.palette.info.main, RuleRoundedIcon)}
              {statChip(`${stats?.excluded ?? 0} excluded`, theme.palette.warning.main, BlockRoundedIcon)}

              {/* online/edit state chip */}
              <Chip
                size="small"
                icon={
                  isEditable ? (
                    <EditNoteRoundedIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
                  )
                }
                label={isEditable ? "Editable" : "View Only"}
                color={isEditable ? "info" : "default"}
                variant="outlined"
                sx={{ height: 28, fontWeight: 800, borderRadius: "8px" }}
              />
            </Stack>

            {/* compact meta tiles: department / created / step / project */}
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{
                mt: 1.4,
                pt: 1.3,
                flexWrap: "wrap",
                borderTop: `1px dashed ${alpha(theme.palette.divider, 0.2)}`,
              }}
            >
              <InfoTile
                icon={BusinessCenterRoundedIcon}
                tone={theme.palette.primary.main}
                label="Department"
                value={employee?.department || "-"}
                title={employee?.department || ""}
              />
              <InfoTile
                icon={EventRoundedIcon}
                tone={theme.palette.info.main}
                label="Created"
                value={
                  appraisal?.created_at
                    ? new Date(appraisal.created_at).toLocaleDateString()
                    : "-"
                }
              />
              <InfoTile
                icon={StairsRoundedIcon}
                tone={theme.palette.secondary.main}
                label="Current Step"
                value={`Step ${appraisal?.current_step_order || "-"}`}
              />
              <InfoTile
                icon={ApartmentIcon}
                tone={theme.palette.success.main}
                label="Project"
                value={resolvedProjectName}
                title={String(resolvedProjectName || "")}
              />
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
